import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiBlob } from './api';
import { i18n } from './i18n';
import { useOrgId } from './org';

/** India GST (document 06 T6–T9). Amounts are decimal strings in INR. */
export interface GstAmounts {
  taxable: string;
  igst: string;
  cgst: string;
  sgst: string;
  cess: string;
}

export interface GstFinding {
  code: string;
  message: string;
  documentId?: string | null;
  number?: string | null;
}

export interface GstMonth {
  period: string;
  from: string;
  to: string;
  gstr1Due: string;
  gstr3bDue: string;
  documents: number;
  outwardTaxable: string;
  outputTax: string;
  itc: string;
  cashPayable: string;
  findings: number;
}

export interface GstInvoice {
  documentId: string | null;
  number: string;
  date: string;
  contactName: string | null;
  gstin: string | null;
  placeOfSupply: string;
  value: string;
  items: Array<GstAmounts & { rate: string }>;
  totals: GstAmounts;
  exportType?: 'WPAY' | 'WOPAY';
  noteType?: 'C' | 'D';
  unregisteredType?: string;
}

export interface Gstr1View {
  period: string;
  from: string;
  to: string;
  gstin: string | null;
  stateCode: string;
  dueDate: string;
  entries: Record<string, { entryId: string | null; kind: string }>;
  b2b: GstInvoice[];
  b2cl: GstInvoice[];
  exp: GstInvoice[];
  b2cs: Array<GstAmounts & { supplyType: 'INTRA' | 'INTER'; placeOfSupply: string; rate: string }>;
  nil: Array<{ supplyType: string; nil: string; exempt: string; nonGst: string }>;
  cdnr: GstInvoice[];
  cdnur: GstInvoice[];
  hsn: {
    b2b: Array<
      GstAmounts & { hsn: string; description: string; uqc: string; quantity: string; rate: string }
    >;
    b2c: Array<
      GstAmounts & { hsn: string; description: string; uqc: string; quantity: string; rate: string }
    >;
  };
  docs: Array<{
    docType: number;
    label: string;
    from: string;
    to: string;
    total: number;
    cancelled: number;
    net: number;
  }>;
  summary: Array<GstAmounts & { table: string; label: string; count: number; value: string }>;
  findings: GstFinding[];
}

export interface GstHeads {
  igst: string;
  cgst: string;
  sgst: string;
  cess: string;
}

export interface Gstr3bView {
  period: string;
  from: string;
  to: string;
  gstin: string | null;
  stateCode: string;
  dueDate: string;
  rows: Array<GstAmounts & { key: string; label: string; documentIds: string[] }>;
  interState: Array<{ placeOfSupply: string; taxable: string; igst: string }>;
  exemptInward: Array<{ type: 'GST' | 'NONGST'; inter: string; intra: string }>;
  setOff: {
    liability: GstHeads;
    paidBy: Record<'igst' | 'cgst' | 'sgst' | 'cess', GstHeads>;
    cash: GstHeads;
    carryForward: GstHeads;
  };
  findings: GstFinding[];
}

export interface IrnRecord {
  id: string;
  irn: string;
  ackNo: string;
  ackDate: string;
  signedQrCode: string;
  provider: string;
  status: 'active' | 'cancelled';
  createdAt: string;
}

/** IRP cancellation reasons (**confirm** against the NIC master). */
export const IRN_CANCEL_REASONS: Array<[number, string]> = [
  [1, 'Duplicate'],
  [2, 'Data entry mistake'],
  [3, 'Order cancelled'],
  [4, 'Others'],
];

/** Shown while IRNs and e-way bills come from the mock connection (document 13, question 54). */
export const testConnectionHint = (): string => i18n.t('taxPages.gstLib.testConnectionHint');

/** An IRN cancellation reason (IRN_CANCEL_REASONS) in the reader's language. */
export function irnCancelReasonLabel(code: number): string {
  const m: Record<number, string> = {
    1: i18n.t('taxPages.gstLib.irnCancel.duplicate'),
    2: i18n.t('taxPages.gstLib.irnCancel.dataEntry'),
    3: i18n.t('taxPages.gstLib.irnCancel.orderCancelled'),
    4: i18n.t('taxPages.gstLib.irnCancel.others'),
  };
  return m[code] ?? String(code);
}

export interface EInvoicePreview {
  json: string;
  validation: {
    valid: boolean;
    errors: Array<{ rule: string; message: string; line?: number }>;
    warnings: Array<{ rule: string; message: string; line?: number }>;
  };
  irn: IrnRecord | null;
  /** The IRN cancelled most recently, shown while no new one is registered. */
  cancelledIrn?: { irn: string; ackNo: string; cancelReason: string; cancelledAt: string } | null;
}

export function useGstMonths() {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['gst', orgId, 'returns'],
    queryFn: () =>
      api<{ gstin: string | null; stateCode: string; months: GstMonth[] }>(
        `/orgs/${orgId}/gst/returns`,
      ),
    enabled: Boolean(orgId),
  });
}

export function useGstr1(period: string) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['gst', orgId, 'gstr1', period],
    queryFn: () => api<Gstr1View>(`/orgs/${orgId}/gst/gstr1?period=${period}`),
    enabled: Boolean(orgId),
  });
}

export function useGstr3b(period: string) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['gst', orgId, 'gstr3b', period],
    queryFn: () => api<Gstr3bView>(`/orgs/${orgId}/gst/gstr3b?period=${period}`),
    enabled: Boolean(orgId),
  });
}

/** Downloads a return in the GSTN offline-tool JSON format. */
export async function downloadReturnJson(
  orgId: string,
  form: 'gstr1' | 'gstr3b',
  period: string,
  gstin: string,
) {
  const blob = await apiBlob(`/orgs/${orgId}/gst/${form}/json?period=${period}`);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${form === 'gstr1' ? 'GSTR1' : 'GSTR3B'}_${gstin}_${period.slice(5, 7)}${period.slice(0, 4)}.json`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function useEInvoicePreview(documentId: string | undefined, enabled: boolean) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['gst', orgId, 'einvoice', documentId],
    queryFn: () => api<EInvoicePreview>(`/orgs/${orgId}/gst/einvoice/${documentId}`),
    enabled: Boolean(orgId && documentId && enabled),
  });
}

export function useIrnAction(documentId: string) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      a: { kind: 'generate' } | { kind: 'cancel'; reasonCode: number; remark: string },
    ) =>
      a.kind === 'generate'
        ? api<IrnRecord>(`/orgs/${orgId}/gst/einvoice/${documentId}/irn`, { method: 'POST' })
        : api<IrnRecord>(`/orgs/${orgId}/gst/einvoice/${documentId}/cancel`, {
            method: 'POST',
            body: JSON.stringify({ reasonCode: a.reasonCode, remark: a.remark }),
          }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['gst', orgId, 'einvoice', documentId] });
      await qc.invalidateQueries({ queryKey: ['sales'] });
    },
  });
}

/** GST states and union territories (codes as in a GSTIN); 96 is a supply outside India. */
export const GST_STATES: Array<[string, string]> = [
  ['01', 'Jammu and Kashmir'],
  ['02', 'Himachal Pradesh'],
  ['03', 'Punjab'],
  ['04', 'Chandigarh'],
  ['05', 'Uttarakhand'],
  ['06', 'Haryana'],
  ['07', 'Delhi'],
  ['08', 'Rajasthan'],
  ['09', 'Uttar Pradesh'],
  ['10', 'Bihar'],
  ['11', 'Sikkim'],
  ['12', 'Arunachal Pradesh'],
  ['13', 'Nagaland'],
  ['14', 'Manipur'],
  ['15', 'Mizoram'],
  ['16', 'Tripura'],
  ['17', 'Meghalaya'],
  ['18', 'Assam'],
  ['19', 'West Bengal'],
  ['20', 'Jharkhand'],
  ['21', 'Odisha'],
  ['22', 'Chhattisgarh'],
  ['23', 'Madhya Pradesh'],
  ['24', 'Gujarat'],
  ['26', 'Dadra and Nagar Haveli and Daman and Diu'],
  ['27', 'Maharashtra'],
  ['29', 'Karnataka'],
  ['30', 'Goa'],
  ['31', 'Lakshadweep'],
  ['32', 'Kerala'],
  ['33', 'Tamil Nadu'],
  ['34', 'Puducherry'],
  ['35', 'Andaman and Nicobar Islands'],
  ['36', 'Telangana'],
  ['37', 'Andhra Pradesh'],
  ['38', 'Ladakh'],
  ['97', 'Other Territory'],
];

/** "27" → "Maharashtra". */
export function stateName(code: string | null | undefined): string {
  return GST_STATES.find(([c]) => c === code)?.[1] ?? code ?? '';
}

/** "27" → "27 · Maharashtra"; "96" → "96 · Other countries". */
export function stateLabel(code: string | null | undefined): string {
  if (!code) return '';
  if (code === '96') return `96 · ${i18n.t('taxPages.gstLib.otherCountries')}`;
  const s = GST_STATES.find(([c]) => c === code);
  return s ? `${s[0]} · ${s[1]}` : code;
}

export const REGISTRATION_TYPES: Array<[string, string]> = [
  ['regular', 'Regular'],
  ['composition', 'Composition'],
  ['sez_unit', 'SEZ unit'],
  ['casual', 'Casual taxable person'],
  ['unregistered', 'Unregistered'],
];

/** A registration type (REGISTRATION_TYPES) in the reader's language. */
export function registrationTypeLabel(key: string): string {
  const m: Record<string, string> = {
    regular: i18n.t('taxPages.gstLib.registration.regular'),
    composition: i18n.t('taxPages.gstLib.registration.composition'),
    sez_unit: i18n.t('taxPages.gstLib.registration.sezUnit'),
    casual: i18n.t('taxPages.gstLib.registration.casual'),
    unregistered: i18n.t('taxPages.gstLib.registration.unregistered'),
  };
  return m[key] ?? key;
}

const GSTIN_CHARS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/**
 * Checks a GSTIN as the API does (tax pack): shape, state code and the check character (Luhn
 * mod 36), so the drawer can say what is wrong before saving. Returns null when it is valid.
 */
export function gstinProblem(input: string): string | null {
  const g = input.replace(/\s+/g, '').toUpperCase();
  if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z][A-Z0-9][0-9A-Z]$/.test(g))
    return i18n.t('taxPages.gstLib.gstinShape');
  if (!GST_STATES.some(([c]) => c === g.slice(0, 2)))
    return i18n.t('taxPages.gstLib.gstinState', { code: g.slice(0, 2) });
  let sum = 0;
  for (let i = 0; i < 14; i++) {
    const p = GSTIN_CHARS.indexOf(g[i]!) * (i % 2 ? 2 : 1);
    sum += Math.floor(p / 36) + (p % 36);
  }
  return GSTIN_CHARS[(36 - (sum % 36)) % 36] === g[14]
    ? null
    : i18n.t('taxPages.gstLib.gstinCheck');
}

/** "2026-09" → "September 2026" ("سبتمبر 2026" for an Arabic reader, Western digits). */
export function monthLabel(period: string): string {
  const [y, m] = period.split('-').map(Number) as [number, number];
  if (i18n.language === 'ar') {
    const month = new Intl.DateTimeFormat('ar-AE', { month: 'long', timeZone: 'UTC' }).format(
      new Date(Date.UTC(y, m - 1, 1)),
    );
    return `${month} ${y}`;
  }
  return new Intl.DateTimeFormat('en-GB', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(y, m - 1, 1)));
}

/** The month before the current one, the usual return to prepare: "2026-09" on 14/10/2026. */
export function lastMonth(today = new Date()): string {
  const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - 1, 1));
  return d.toISOString().slice(0, 7);
}

export function shiftMonth(period: string, n: number): string {
  const [y, m] = period.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(y, m - 1 + n, 1)).toISOString().slice(0, 7);
}

/** "2026-09" → first and last day of the month. */
export function periodRange(period: string): { from: string; to: string } {
  const [y, m] = period.split('-').map(Number) as [number, number];
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { from: `${period}-01`, to: `${period}-${String(last).padStart(2, '0')}` };
}

// ---------- e-way bills (document 06 T10, T11; migration 1810) ----------

export interface EwayBill {
  id: string;
  documentId: string;
  ewbNo: string;
  ewbDate: string;
  /** Null while only Part A exists (no vehicle or transport document). */
  validUntil: string | null;
  generatedVia: 'irn' | 'ewb';
  subSupplyType: number;
  docType: string;
  distanceKm: number;
  transMode: number | null;
  vehicleNo: string | null;
  vehicleType: 'R' | 'O';
  transporterId: string | null;
  transporterName: string | null;
  transDocNo: string | null;
  transDocDate: string | null;
  provider: string;
  status: 'active' | 'cancelled';
  cancelReason: number | null;
  cancelRemark: string | null;
  cancelledAt: string | null;
  cancellable: boolean;
  vehicles: Array<{
    vehicleNo: string | null;
    transMode: number;
    transDocNo: string | null;
    fromPlace: string;
    fromState: string;
    reasonCode: number;
    reasonRemark: string | null;
    validUntil: string;
    createdAt: string;
  }>;
}

export interface EwayBillStatus {
  /** False when every line is a service (SAC): no e-way bill applies. */
  goods: boolean;
  required: { required: boolean; reason: string };
  defaults: {
    subSupplyType: number;
    subSupplyDesc: string | null;
    docType: string;
    value: string;
    fromPincode: string;
    toPincode: string;
  };
  irn: string | null;
  ewayBill: EwayBill | null;
}

export interface EwayBillRow extends EwayBill {
  documentNumber: string | null;
  documentKind: string;
  contactName: string;
  total: string;
  expired: boolean;
}

export interface EwbTransport {
  distanceKm: number;
  transMode: number | null;
  vehicleNo: string | null;
  vehicleType: 'R' | 'O';
  transporterId: string | null;
  transporterName: string | null;
  transDocNo: string | null;
  transDocDate: string | null;
}

/** NIC code tables (**confirm** against the e-way bill system's masters). */
export const EWB_MODES: Array<[number, string]> = [
  [1, 'Road'],
  [2, 'Rail'],
  [3, 'Air'],
  [4, 'Ship'],
];
export const EWB_CANCEL_REASONS: Array<[number, string]> = [
  [1, 'Duplicate'],
  [2, 'Order cancelled'],
  [3, 'Data entry mistake'],
  [4, 'Others'],
];
export const EWB_VEHICLE_REASONS: Array<[number, string]> = [
  [1, 'Breakdown'],
  [2, 'Transhipment'],
  [3, 'Others'],
  [4, 'First time'],
];

/** A transport mode (EWB_MODES) in the reader's language. */
export function ewbModeLabel(code: number): string {
  const m: Record<number, string> = {
    1: i18n.t('taxPages.gstLib.mode.road'),
    2: i18n.t('taxPages.gstLib.mode.rail'),
    3: i18n.t('taxPages.gstLib.mode.air'),
    4: i18n.t('taxPages.gstLib.mode.ship'),
  };
  return m[code] ?? String(code);
}

/** An e-way bill cancellation reason (EWB_CANCEL_REASONS) in the reader's language. */
export function ewbCancelReasonLabel(code: number): string {
  const m: Record<number, string> = {
    1: i18n.t('taxPages.gstLib.irnCancel.duplicate'),
    2: i18n.t('taxPages.gstLib.irnCancel.orderCancelled'),
    3: i18n.t('taxPages.gstLib.irnCancel.dataEntry'),
    4: i18n.t('taxPages.gstLib.irnCancel.others'),
  };
  return m[code] ?? String(code);
}

/** A reason for changing the vehicle (EWB_VEHICLE_REASONS) in the reader's language. */
export function ewbVehicleReasonLabel(code: number): string {
  const m: Record<number, string> = {
    1: i18n.t('taxPages.gstLib.vehicleReason.breakdown'),
    2: i18n.t('taxPages.gstLib.vehicleReason.transhipment'),
    3: i18n.t('taxPages.gstLib.irnCancel.others'),
    4: i18n.t('taxPages.gstLib.vehicleReason.firstTime'),
  };
  return m[code] ?? String(code);
}

/** A sub-supply type (NIC code table) in the reader's language. */
export function ewbSubSupplyLabel(code: number): string {
  const m: Record<number, string> = {
    1: i18n.t('taxPages.gstLib.subSupply.s1'),
    2: i18n.t('taxPages.gstLib.subSupply.s2'),
    3: i18n.t('taxPages.gstLib.subSupply.s3'),
    4: i18n.t('taxPages.gstLib.subSupply.s4'),
    5: i18n.t('taxPages.gstLib.subSupply.s5'),
    6: i18n.t('taxPages.gstLib.subSupply.s6'),
    7: i18n.t('taxPages.gstLib.subSupply.s7'),
    8: i18n.t('taxPages.gstLib.subSupply.s8'),
    9: i18n.t('taxPages.gstLib.subSupply.s9'),
    10: i18n.t('taxPages.gstLib.subSupply.s10'),
    11: i18n.t('taxPages.gstLib.subSupply.s11'),
    12: i18n.t('taxPages.gstLib.subSupply.s12'),
  };
  return m[code] ?? String(code);
}

export function useEwayBill(documentId: string | undefined, enabled: boolean) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['gst', orgId, 'eway-bill', documentId],
    queryFn: () => api<EwayBillStatus>(`/orgs/${orgId}/gst/eway-bill/${documentId}`),
    enabled: Boolean(orgId && documentId && enabled),
  });
}

export function useEwayBills() {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['gst', orgId, 'eway-bills'],
    queryFn: () => api<EwayBillRow[]>(`/orgs/${orgId}/gst/eway-bills`),
    enabled: Boolean(orgId),
  });
}

type EwbAction =
  | { kind: 'generate'; transport: EwbTransport }
  | {
      kind: 'vehicle';
      update: {
        transMode: number;
        vehicleNo: string | null;
        transDocNo: string | null;
        transDocDate: string | null;
        fromPlace: string;
        fromState: string;
        reasonCode: number;
        reasonRemark: string | null;
        distanceKm: number;
      };
    }
  | { kind: 'cancel'; reasonCode: number; remark: string };

export function useEwayBillAction(documentId: string) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: EwbAction) =>
      api<EwayBill>(
        `/orgs/${orgId}/gst/eway-bill/${documentId}${
          a.kind === 'generate' ? '' : a.kind === 'vehicle' ? '/vehicle' : '/cancel'
        }`,
        {
          method: 'POST',
          body: JSON.stringify(
            a.kind === 'generate'
              ? a.transport
              : a.kind === 'vehicle'
                ? a.update
                : { reasonCode: a.reasonCode, remark: a.remark },
          ),
        },
      ),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['gst', orgId] });
      await qc.invalidateQueries({ queryKey: ['sales'] });
    },
  });
}

// ---------- IMS / GSTR-2B worksheet (document 06 T12; migration 1820) ----------

export type ImsAction = 'accept' | 'reject' | 'pending' | 'no_action';
export type ImsMatch = 'matched' | 'mismatch' | 'probable' | 'not_in_books';

export interface ImsRow {
  id: string;
  section: string;
  supplierGstin: string;
  supplierName: string | null;
  docType: 'invoice' | 'credit_note' | 'debit_note';
  number: string;
  date: string;
  total: string;
  taxable: string;
  igst: string;
  cgst: string;
  sgst: string;
  cess: string;
  reverseCharge: boolean;
  itcAvailable: boolean | null;
  itcReason: string | null;
  matchStatus: ImsMatch;
  differences: Array<{ field: string; portal: string; books: string }>;
  matched: {
    id: string;
    kind: string | null;
    number: string | null;
    reference: string | null;
  } | null;
  action: ImsAction;
  actionRemark: string | null;
  actionBy: string | null;
  actionAt: string | null;
}

type ImsTotals = GstAmounts & { count: number };

export interface ImsView {
  period: string;
  gstin: string | null;
  imports: Array<{
    id: string;
    source: 'gstr2b' | 'csv';
    fileName: string | null;
    rows: number;
    warnings: string[];
    createdAt: string;
  }>;
  summary: {
    matched: number;
    mismatch: number;
    probable: number;
    notInBooks: number;
    notOnPortal: number;
    itc: { byAction: Record<ImsAction, ImsTotals>; itcAvailable: ImsTotals };
    booksItc: { igst: string; cgst: string; sgst: string; cess: string };
  };
  rows: ImsRow[];
  notOnPortal: Array<{
    id: string;
    kind: string;
    number: string | null;
    vendor: string;
    supplierGstin: string;
    reference: string;
    date: string;
    taxable: string;
    tax: string;
  }>;
}

export function useIms(period: string) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['gst', orgId, 'ims', period],
    queryFn: () => api<ImsView>(`/orgs/${orgId}/gst/ims?period=${period}`),
    enabled: Boolean(orgId),
  });
}

export function useImsMutations(period: string) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const done = async () => {
    await qc.invalidateQueries({ queryKey: ['gst', orgId, 'ims'] });
  };
  return {
    importFile: useMutation({
      mutationFn: (f: { source: 'gstr2b' | 'csv'; fileName: string; content: string }) =>
        api<ImsView>(`/orgs/${orgId}/gst/ims/import`, {
          method: 'POST',
          body: JSON.stringify({ ...f, period }),
        }),
      onSuccess: done,
    }),
    rematch: useMutation({
      mutationFn: () =>
        api<ImsView>(`/orgs/${orgId}/gst/ims/rematch?period=${period}`, { method: 'POST' }),
      onSuccess: done,
    }),
    act: useMutation({
      mutationFn: (a: { ids: string[]; action: ImsAction; remark?: string }) =>
        api<{ updated: number }>(`/orgs/${orgId}/gst/ims/actions`, {
          method: 'POST',
          body: JSON.stringify(a),
        }),
      onSuccess: done,
    }),
  };
}

/** Downloads the IMS worksheet of a month as CSV. */
export async function downloadImsCsv(orgId: string, period: string, gstin: string) {
  const blob = await apiBlob(`/orgs/${orgId}/gst/ims/csv?period=${period}`);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `IMS_${gstin}_${period.slice(5, 7)}${period.slice(0, 4)}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** The inward supplies CSV template (document 07, "Invoice Management System"). */
export const IMS_CSV_HEADERS = [
  'Supplier GSTIN',
  'Supplier name',
  'Document type',
  'Document number',
  'Document date',
  'Document value',
  'Place of supply',
  'Reverse charge',
  'Taxable value',
  'IGST',
  'CGST',
  'SGST',
  'Cess',
  'ITC available',
];
